import { parseDateValue } from '@/lib/utils';
import type { Task, TaskCounts } from '@/services/tasks.service';
import type { AdminUser, AccountRoleEntry } from '@/services/admin.service';
import type { BadgeVariant } from '@/types/common.types';
import type { FilterConfig, FilterOption } from '@/types/filters.types';

export const TASK_FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search patients...' },
  { key: 'dateRange', label: 'Date Range', type: 'daterange' },
  { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
  { key: 'memberStatus', label: 'Member Status', type: 'select', allLabel: 'All statuses', multi: true },
  { key: 'assignedTo', label: 'Assigned To', type: 'select', allLabel: 'All assignees', multi: true },
  { key: 'priority', label: 'Priority', type: 'select', allLabel: 'All priorities', multi: true },
  { key: 'taskGroup', label: 'Task Group', type: 'select', allLabel: 'All groups', multi: true },
];

/**
 * Build the Assigned To filter options. Tiered by user level:
 * - Non-admins: "Me" + only the user's own roles (matching legacy filter-roles.php)
 * - Admins (level 3+): "Me" + ALL account roles + ALL active users
 *
 * When allRoles/allUsers are provided, the admin path is used.
 */
export function buildAssignedToOptions(
  myRoles: AccountRoleEntry[] | null | undefined,
  allRoles?: AccountRoleEntry[] | null,
  allUsers?: AdminUser[] | null,
): FilterOption[] {
  if (!allRoles && !allUsers) {
    const roleOpts = (myRoles ?? [])
      .map((r) => ({ value: `role-${r.roleId}`, label: r.roleTitle }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: 'me', label: 'Me' }, ...roleOpts];
  }
  const myRoleIds = new Set((myRoles ?? []).map((r) => r.roleId));
  const myRoleOpts = (allRoles ?? [])
    .filter((r) => myRoleIds.has(r.roleId))
    .map((r) => ({ value: `role-${r.roleId}`, label: `${r.roleTitle} (Me)`, group: 'USER GROUPS' }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const otherRoleOpts = (allRoles ?? [])
    .filter((r) => !myRoleIds.has(r.roleId))
    .map((r) => ({ value: `role-${r.roleId}`, label: r.roleTitle, group: 'USER GROUPS' }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const userOpts = (allUsers ?? [])
    .filter((u) => u.active)
    .map((u) => ({
      value: `user-${u.userId}`,
      label: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.userName || `User #${u.userId}`,
      group: 'USERS',
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return [{ value: 'me', label: 'Me' }, ...myRoleOpts, ...otherRoleOpts, ...userOpts];
}

// Every tab renders the same task table with the same columns — only the
// bucket differs. Past Due and Upcoming used to render a grouped-by-patient
// table with its own unsortable column set, which made the tabs disagree about
// what a "task list" looks like.
export const TASK_FILTER_TABS: Array<{
  value: number;
  label: string;
  countKey: keyof TaskCounts;
  tooltip: string;
  headerTitle: string;
  showBadge: boolean;
}> = [
  { value: 9, label: 'Past Due',    countKey: 'pastDue',   tooltip: 'Tasks whose due date has passed and are still pending', headerTitle: 'Past Due Tasks',  showBadge: true  },
  { value: 3, label: 'Due Today',   countKey: 'dueToday',  tooltip: 'Tasks that are due today and need attention',           headerTitle: 'Due Today Tasks', showBadge: true  },
  { value: 10, label: 'Upcoming',   countKey: 'upcoming',  tooltip: 'Pending tasks with a future due date',                  headerTitle: 'Upcoming Tasks',  showBadge: true  },
  { value: 5, label: 'Complete',    countKey: 'completed', tooltip: 'Tasks that have been marked as completed',              headerTitle: 'Completed Tasks', showBadge: false },
  { value: 6, label: 'Inactive',    countKey: 'inactive',  tooltip: 'Tasks that are not active or have no due date set',     headerTitle: 'Inactive Tasks',  showBadge: false },
  { value: 1, label: 'All Entries', countKey: 'all',       tooltip: 'View all tasks regardless of status',                   headerTitle: 'Tasks',           showBadge: false },
];

export function parseTaskFilterTab(value: string | null | undefined): number {
  const parsed = Number(value);
  if (TASK_FILTER_TABS.some((tab) => tab.value === parsed)) return parsed;
  if (parsed === 4) return 9;
  if (parsed === 2) return 10;
  return 1;
}

export const TASK_MEMBER_STATUS_OPTIONS: FilterOption[] = [
  { value: 'member', label: 'Member' },
  { value: 'non-member', label: 'Non-member' },
];

/**
 * `flTasks` bucket for every task still awaiting action — Past Due + Due Today +
 * Upcoming (TaskDashboardService::FL_PENDING). Not one of the TASK_FILTER_TABS:
 * it exists for the dashboard task cards, which must not default to FL_UPCOMING
 * (future-dated only) and thereby hide overdue work.
 */
export const TASK_BUCKET_PENDING = 11;

export function buildAllTasksHref(flTasks?: number): string {
  return flTasks && flTasks !== 1 ? `/tasks/all?flTasks=${flTasks}` : '/tasks/all';
}

// ─── Task status / priority / due-date helpers ───────────────────────────────
// Shared by the tasks list, both dashboard task cards, and the task modal so
// they label, colour, and gate tasks identically.

export type TaskStatusLabel = 'Past Due' | 'Due' | 'Upcoming' | 'Completed' | 'Inactive';

const STATUS_LABELS: readonly TaskStatusLabel[] = ['Past Due', 'Due', 'Upcoming', 'Completed', 'Inactive'];

export function statusVariant(s?: string): BadgeVariant {
  switch (s) {
    case 'Past Due': return 'error';
    case 'Due': return 'warning';
    case 'Upcoming': return 'info';
    case 'Completed': return 'success';
    case 'Inactive': default: return 'neutral';
  }
}

export function priorityLabel(p: number): string {
  switch (p) {
    case 3: return 'High';
    case 2: return 'Medium';
    case 1: default: return 'Low';
  }
}

export function priorityVariant(p: number): BadgeVariant {
  switch (p) {
    case 3: return 'error';
    case 2: return 'warning';
    default: return 'neutral';
  }
}

/**
 * The task's real due date. `lastDue` wins over `customDate`, matching the Due
 * column on /tasks/all. Must compose on the *parsed* values: legacy sends the
 * truthy string '0000-00-00' for no-due rows, so `lastDue ?? customDate` would
 * never fall through. parseDateValue also reads a date-only "YYYY-MM-DD" as
 * local midnight, avoiding the UTC off-by-one that rendered dates a day early.
 */
export function taskDueDate(t: Pick<Task, 'lastDue' | 'customDate'>): Date | null {
  return parseDateValue(t.lastDue) ?? parseDateValue(t.customDate);
}

/**
 * Resolve a task's status label. Trusts the server's `derivedStatus` when it is
 * one of the five known values, otherwise mirrors the same CASE expression
 * (TaskDashboardService::listTasks) client-side — GET /plan-tasks/{id} returns a
 * differently-worded `status` and no `derivedStatus`, and tasks sourced from the
 * Plans tab may carry neither.
 */
export function taskStatusLabel(
  t: Pick<Task, 'derivedStatus' | 'pending' | 'dateCompleted' | 'lastDue' | 'customDate'>,
): TaskStatusLabel {
  if (t.derivedStatus && (STATUS_LABELS as readonly string[]).includes(t.derivedStatus)) {
    return t.derivedStatus as TaskStatusLabel;
  }

  const completed = parseDateValue(t.dateCompleted) !== null;
  if (t.pending === 0) return completed ? 'Completed' : 'Inactive';
  if (t.pending === 2) return 'Inactive';

  const due = taskDueDate(t);
  if (!due) return 'Inactive';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDay = new Date(due);
  dueDay.setHours(0, 0, 0, 0);

  if (dueDay.getTime() < today.getTime()) return 'Past Due';
  if (dueDay.getTime() === today.getTime()) return 'Due';
  return 'Upcoming';
}

/**
 * Whether the task is still open — i.e. NOT in a terminal state. `Completed`
 * and `Inactive` are the two labels that mean "this task is closed"; a closed
 * task can no longer be completed, de-activated, rescheduled or edited.
 *
 * This is the single rule for "can this task still be acted on", so the modal's
 * read-only lock, the Done gate and the list checkboxes cannot drift apart.
 * Note it deliberately reads the *label*, not `pending === 0`: a row also reads
 * as Inactive at pending = 2, and at pending = 1 with no due date at all.
 */
export function isTaskOpen(
  t: Pick<Task, 'derivedStatus' | 'pending' | 'dateCompleted' | 'lastDue' | 'customDate'>,
): boolean {
  const label = taskStatusLabel(t);
  return label !== 'Completed' && label !== 'Inactive';
}

/**
 * Whether a Done/Complete affordance should be offered. Mirrors the rule used
 * on the patient Tasks tab and Plans tab: never for a closed task, and not for
 * a not-yet-due task whose `required` flag forbids early completion (legacy
 * inverted enum — 1 means "Complete Before Due: No").
 */
export function canCompleteTask(
  t: Pick<Task, 'derivedStatus' | 'pending' | 'dateCompleted' | 'lastDue' | 'customDate' | 'required'>,
): boolean {
  if (!isTaskOpen(t)) return false;
  return !(taskStatusLabel(t) === 'Upcoming' && t.required === 1);
}

/**
 * Normalize a plan identifier for display, returning null when the task has no
 * plan. Legacy stores "no plan" as the sentinel 0 rather than NULL, and the
 * task list endpoint sends planDisplayID as a *string* — so the value arrives
 * as "0", which is truthy in JS and rendered as "#0". Handles both the string
 * and number shapes so neither field can leak the sentinel into the UI.
 */
export function planNumberOrNull(value: string | number | null | undefined): string | null {
  const text = String(value ?? '').trim();
  return text === '' || text === '0' ? null : text;
}
